import React, { useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import {
  Button,
  Dialog,
  Icon,
  ModalBody,
  Modal as ModalComponent,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  Switch,
  Textarea,
  Textfield
} from '@clientbase/clientbase-library'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material'
import { useCustomFieldPermissions } from 'hooks/useCustomFieldPermissions'
import {
  ExtraFieldsService,
  PayloadCreateExtraField
} from 'services/ExtraFieldsService'
import { v4 as uuid } from 'uuid'

import IconContainer from 'components/IconContainer/IconContainer'
import RadioBoxContainer from 'components/RadioBoxContainer/RadioBoxContainer'
import Upgrade from 'components/Upgrade'
import UploadComponent from 'components/UploadComponent'
import { FileParams } from 'components/UploadComponent/FileList'

import { normalizeString } from 'utils'
import {
  formatUploadMaxSize,
  normalizeAcceptedTypes,
  UPLOAD_DEFAULT_MAX_FILES,
  UPLOAD_FILE_TYPE_KEYS,
  UPLOAD_FILE_TYPES,
  UPLOAD_MAX_FILE_SIZE,
  UPLOAD_MAX_FILES_LIMIT,
  UPLOAD_MIN_MULTIPLE_FILES,
  UPLOAD_NEW_FIELD_DEFAULT_TYPES,
  UploadFileType
} from 'utils/customFieldUpload'
import { toastFirstError } from 'utils/yupResolverWithToast'

import { theme } from 'styles/theme'

import {
  ExtraFieldState,
  useExtraFieldsContext
} from '../../Extrafields.context'
import ExtraFieldEditSkeleton from '../ExtraFieldsSkeleton/EditSkeleton/ExtraFieldsSkeleton'
import DisplayModeTooltip from './DisplayModeTooltip'
import { extraFieldsSchema, ExtraFieldsSchemaType } from './ExtraFields.schema'
import * as S from './ExtraFields.styles'

const OptionsFieldType = [
  {
    label: (
      <Stack direction="row" gap={0.5} mt="2px" alignItems="center">
        <Typography variant="smLight">Texto curto</Typography>
        <Typography variant="smLight" color={theme.palette.neutral[600]}>
          (Ideal para coletar poucas informações descritivas)
        </Typography>
      </Stack>
    ),
    value: 'short_text'
  },
  {
    label: (
      <Stack direction="row" mt="2px" gap={0.5} alignItems="center">
        <Typography variant="smLight">Texto longo</Typography>
        <Typography variant="smLight" color={theme.palette.neutral[600]}>
          (Ideal para coletar muitas informações descritivas)
        </Typography>
      </Stack>
    ),
    value: 'long_text'
  },
  {
    label: (
      <Stack direction="row" mt="2px" gap={0.5} alignItems="center">
        <Typography variant="smLight">Multi seleção</Typography>
        <Typography variant="smLight" color={theme.palette.neutral[600]}>
          (Seu cliente pode escolher mais de uma opção)
        </Typography>
      </Stack>
    ),
    value: 'checkbox'
  },
  {
    label: (
      <Stack direction="row" mt="2px" gap={0.5} alignItems="center">
        <Typography variant="smLight">Seleção única</Typography>
        <Typography variant="smLight" color={theme.palette.neutral[600]}>
          (Seu cliente só pode escolher uma opção)
        </Typography>
      </Stack>
    ),
    value: 'radio'
  },
  {
    label: (
      <Stack direction="row" mt="2px" gap={0.5} alignItems="center">
        <Typography variant="smLight">Termos e Condições</Typography>
        <Typography variant="smLight" color={theme.palette.neutral[600]}>
          (Ideal para garantir que seu cliente tenha ciência de alguma cláusula)
        </Typography>
      </Stack>
    ),
    value: 'accept'
  },
  {
    label: (
      <Stack direction="row" mt="2px" gap={0.5} alignItems="center">
        <Typography variant="smLight">Upload de arquivo</Typography>
        <Typography variant="smLight" color={theme.palette.neutral[600]}>
          (Quando você precisa que seu cliente forneça algum documento)
        </Typography>
      </Stack>
    ),
    value: 'upload'
  },
  {
    label: (
      <Stack direction="row" mt="2px" gap={0.5} alignItems="center">
        <Typography variant="smLight">Data</Typography>
        <Typography variant="smLight" color={theme.palette.neutral[600]}>
          (Ideal para coletar datas como nascimento, vencimento, etc.)
        </Typography>
      </Stack>
    ),
    value: 'date'
  }
]
interface ModalProps {
  defaultName?: string
  onFieldCreated?: (fieldUuid: string) => void
  onFieldUpdated?: (field: ExtraFieldState) => void
  onModalClose?: () => void
}
const Modal: React.FC<ModalProps> = ({
  defaultName,
  onFieldCreated,
  onFieldUpdated,
  onModalClose
}: ModalProps) => {
  const isSM = useMediaQuery(theme.breakpoints.down('sm'))
  const [dialogOpen, setDialogOpen] = useState(false)
  const [uploadIsLoading, setUploadIsLoading] = useState<boolean>(false)
  const [acceptTypeValue, setAcceptTypeValue] = useState<
    'link' | 'text' | 'file' | 'full_date' | 'month_year' | 'year_only'
  >('text')
  const {
    table: { refetch },
    edit: { handleClose, loading, data, open },
    details
  } = useExtraFieldsContext()
  const { getFieldTypePermission } = useCustomFieldPermissions()
  const onClose = () => {
    reset()
    setAcceptTypeValue('text')
    setFiles([])
    handleClose()
    onModalClose?.()
  }
  const [files, setFiles] = useState<FileParams[]>([])
  const [loadingSubmit, setLoadingSubmit] = React.useState(false)

  const uploadRef = useRef<UploadComponent | null>(null)

  const { control, watch, handleSubmit, reset, setValue, formState } =
    useForm<ExtraFieldsSchemaType>({
      defaultValues: {
        field_type: 'short_text',
        title: defaultName ? defaultName : '',
        description: '',
        options: [{ label: '', value: '', uuid: uuid() }],
        value: '',
        type: 'text',
        display_mode: 'list',
        accepted_types: [...UPLOAD_NEW_FIELD_DEFAULT_TYPES],
        multiple: false,
        max_files: UPLOAD_DEFAULT_MAX_FILES
      },
      resolver: yupResolver(extraFieldsSchema)
    })

  const field_type = watch('field_type')
  useEffect(() => {
    if (field_type === 'checkbox' || field_type === 'radio') {
      const currentOptions = watch('options') || []

      if (currentOptions.length < 2) {
        setValue('options', [
          { label: '', value: '', uuid: uuid() },
          { label: '', value: '', uuid: uuid() }
        ])
      }
    }
    if (field_type === 'date') {
      const currentType = watch('type')
      if (
        !currentType ||
        !['full_date', 'month_year', 'year_only'].includes(currentType)
      ) {
        setValue('type', 'full_date')
      }
    }
  }, [field_type])

  const FieldTypeParse = {
    short_text: 0,
    radio: 1,
    checkbox: 2,
    accept: 3,
    upload: 4,
    long_text: 5,
    date: 6
  }

  const FieldTypeReverse = {
    0: 'short_text',
    1: 'radio',
    2: 'checkbox',
    3: 'accept',
    4: 'upload',
    5: 'long_text',
    6: 'date'
  }

  const onSubmit = async (dataForm: ExtraFieldsSchemaType) => {
    try {
      setLoadingSubmit(true)

      const payload = {
        field_type:
          FieldTypeParse[dataForm.field_type as keyof typeof FieldTypeParse],
        field_configuration: {
          title: dataForm.title,
          description: dataForm.description,
          accept_text: dataForm.accept_text
        },

        document_uuid: dataForm?.value
      } as PayloadCreateExtraField

      if (
        dataForm.field_type === 'radio' ||
        dataForm.field_type === 'checkbox'
      ) {
        payload.field_configuration.options = dataForm.options
      }

      if (dataForm.field_type === 'checkbox') {
        payload.field_configuration.display_mode =
          (dataForm.display_mode as 'list' | 'dropdown') || 'list'
      }

      if (dataForm.field_type === 'accept') {
        payload.field_configuration.type = dataForm.type as any
        payload.field_configuration.value = dataForm.value
      }

      if (dataForm.field_type === 'date') {
        payload.field_configuration.type = dataForm.type as any
      }

      if (dataForm.field_type === 'upload') {
        const multiple = !!dataForm.multiple

        payload.field_configuration.accepted_types = normalizeAcceptedTypes(
          dataForm.accepted_types
        )
        payload.field_configuration.multiple = multiple
        if (multiple) {
          payload.field_configuration.max_files = Number(
            dataForm.max_files || UPLOAD_DEFAULT_MAX_FILES
          )
        }
      }

      let response: any

      if (data && data.uuid) {
        response = await ExtraFieldsService.update(data.uuid, payload)
      } else {
        response = await ExtraFieldsService.create(payload)
      }

      if (response.error) {
        toast.error(response.message)
      } else {
        reset()
        refetch()
        handleClose()
        toast.success(response.message)
        details.handleOpen(response.record.uuid)

        // Chama o callback se foi uma criação (não edição) e o callback existe
        if (!data?.uuid && onFieldCreated) {
          onFieldCreated(response.record.uuid)
        }

        if (data?.uuid) {
          onFieldUpdated?.(response.record)
        }
      }
    } catch (error) {
      console.error(error)
    } finally {
      setLoadingSubmit(false)
    }
  }
  const resetFiles = () => {
    const uploadedFiles = uploadRef.current?.state?.uploadedFiles
    if (uploadedFiles && uploadedFiles?.length > 0) {
      uploadRef.current?.handleDelete(uploadedFiles?.[0].id)
    }
  }
  const handleClickToSubmit = async () => {
    if (data?.uuid) {
      setDialogOpen(true)
    } else {
      handleSubmit(onSubmit, toastFirstError)()
    }
  }
  const handleDeleteOption = (uuid: string) => {
    const currentOptions = watch('options') ?? []
    const currentFieldType = watch('field_type')

    // Para campos radio e checkbox, não permitir exclusão se restarem menos de 2 opções
    if (
      (currentFieldType === 'radio' || currentFieldType === 'checkbox') &&
      currentOptions.length <= 2
    ) {
      return
    }

    const newOptions = currentOptions.filter((option) => option.uuid !== uuid)
    setValue('options', newOptions)
  }

  useEffect(() => {
    if (files?.length > 0) {
      setValue('type', 'file')
      setValue('value', files?.[0]?.id)
    }
  }, [files])

  useEffect(() => {
    let fieldTypeValue: string

    if (typeof data?.field_type === 'number') {
      fieldTypeValue =
        FieldTypeReverse[data.field_type as keyof typeof FieldTypeReverse] ||
        'short_text'
    } else if (typeof data?.field_type === 'string') {
      const validTypes = [
        'text',
        'short_text',
        'long_text',
        'radio',
        'checkbox',
        'accept',
        'upload',
        'date'
      ]

      if (validTypes.includes(data.field_type)) {
        fieldTypeValue =
          data.field_type === 'text' ? 'short_text' : data.field_type
      } else {
        fieldTypeValue = 'short_text'
      }
    } else {
      fieldTypeValue = 'short_text'
    }

    const defaultOptions =
      fieldTypeValue === 'checkbox' &&
      !data?.field_configuration?.options?.length
        ? [
            { label: '', value: '', uuid: uuid() },
            { label: '', value: '', uuid: uuid() }
          ]
        : [{ label: '', value: '', uuid: uuid() }]

    const savedAcceptedTypes = data?.field_configuration?.accepted_types
    // Campo de upload antigo (sem a chave) aceita todos os tipos; campo novo
    // começa só com imagem e PDF.
    const acceptedTypes = savedAcceptedTypes?.length
      ? normalizeAcceptedTypes(savedAcceptedTypes)
      : data && fieldTypeValue === 'upload'
      ? [...UPLOAD_FILE_TYPE_KEYS]
      : [...UPLOAD_NEW_FIELD_DEFAULT_TYPES]
    const savedMultiple = data?.field_configuration?.multiple as unknown

    reset({
      field_type: fieldTypeValue,
      title: defaultName ? defaultName : data?.field_configuration?.title || '',
      accept_text: data?.field_configuration?.accept_text || '',
      description: data?.field_configuration?.description || '',
      options:
        data?.field_configuration?.options?.map((option) => ({
          ...option
        })) || defaultOptions,
      value: data?.field_configuration?.value,
      type: data?.field_configuration?.type || 'text',
      display_mode: data?.field_configuration?.display_mode || 'list',
      accepted_types: acceptedTypes,
      multiple: savedMultiple === true || savedMultiple === 'true',
      max_files:
        Number(data?.field_configuration?.max_files) || UPLOAD_DEFAULT_MAX_FILES
    })
    setAcceptTypeValue(data?.field_configuration?.type || 'text')
    if (data?.document_file) {
      setFiles([
        {
          id: data?.document_file?.uuid || '',
          name: data?.document_file?.doc_file_name || '',
          readableSize: data?.document_file?.doc_file_size || 0,
          preview: data?.document_file?.doc_file_url || '',
          uploaded: true,
          url: data?.document_file?.doc_file_url || '',
          error: false,
          progress: 100,
          file: new File([], data?.document_file?.doc_file_name || '')
        }
      ])
    } else {
      setFiles([])
    }
  }, [data])

  return (
    <ModalComponent
      maxWidth="1000px"
      width="100%"
      open={open}
      maxHeight="60vh"
      disablePortal={false}
      handleClose={onClose}
    >
      {loading ? (
        <Box height="45rem">
          <ExtraFieldEditSkeleton />
        </Box>
      ) : (
        <ModalBody
          minHeight="60vh"
          component="form"
          onSubmit={(e) => {
            e.preventDefault()
            handleSubmit(onSubmit, toastFirstError)()
          }}
        >
          <ModalHeader onClose={onClose}>
            <Stack direction="row" alignItems="center" gap={2}>
              <IconContainer icon={data ? 'edit' : 'faCirclePlus'} />
              <Typography variant="mdBold">
                {data ? 'Editar' : 'Novo'} Campo Extra
              </Typography>
            </Stack>
          </ModalHeader>
          <ModalContent>
            <S.Wrapper>
              <Controller
                control={control}
                name="field_type"
                render={({ field }) => {
                  return (
                    <Select
                      {...field}
                      label="Tipo do campo"
                      placeholder="Texto curto (Ideal para coletar poucas informações descritivas)"
                      fullWidth
                    >
                      {OptionsFieldType.map(({ label, value }, index) => {
                        const permission = getFieldTypePermission(value as any)
                        return (
                          <MenuItem
                            key={index}
                            value={value}
                            disabled={permission.isDisabled}
                            sx={{
                              pointerEvents: permission.isDisabled
                                ? 'auto'
                                : 'inherit'
                            }}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              gap={1}
                              width="100%"
                              justifyContent="space-between"
                            >
                              <span>{label}</span>
                              {permission.isDisabled && (
                                <Upgrade
                                  plan={permission.plan as any}
                                  resourceName={`Campo ${label}`}
                                />
                              )}
                            </Stack>
                          </MenuItem>
                        )
                      })}
                    </Select>
                  )
                }}
              />
              <Controller
                control={control}
                name="title"
                render={({ field, fieldState }) => (
                  <Textfield
                    {...field}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    label={
                      <Stack direction="row" alignItems="end" gap={0.5}>
                        <Typography
                          color={theme.palette.neutral[200]}
                          variant="smLight"
                        >
                          Título do campo
                        </Typography>
                      </Stack>
                    }
                    placeholder="Digite aqui o título do campo"
                    fullWidth
                  />
                )}
              />
              <Controller
                control={control}
                name="description"
                render={({ field }) => (
                  <Textfield
                    {...field}
                    label={
                      <Stack direction="row" alignItems="end" gap={0.5}>
                        <Typography
                          color={theme.palette.neutral[200]}
                          variant="smLight"
                        >
                          Descrição do campo -
                        </Typography>
                        <Typography variant="smLight" color="#777777">
                          Opcional
                        </Typography>
                      </Stack>
                    }
                    placeholder="Digite aqui a descrição do campo"
                    fullWidth
                  />
                )}
              />
              {field_type === 'date' && (
                <Controller
                  control={control}
                  name="type"
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={field.value || 'full_date'}
                      label={
                        <Typography
                          color={theme.palette.neutral[200]}
                          variant="smLight"
                        >
                          Formato da data
                        </Typography>
                      }
                      fullWidth
                    >
                      <MenuItem value="full_date">
                        <Typography variant="smLight">Dia/Mês/Ano</Typography>
                      </MenuItem>
                      <MenuItem value="month_year">
                        <Typography variant="smLight">Mês/Ano</Typography>
                      </MenuItem>
                      <MenuItem value="year_only">
                        <Typography variant="smLight">Apenas Ano</Typography>
                      </MenuItem>
                    </Select>
                  )}
                />
              )}
              {field_type === 'checkbox' && (
                <Controller
                  control={control}
                  name="display_mode"
                  render={({ field }) => (
                    <Select
                      {...field}
                      label={
                        <Stack direction="row" alignItems="center" gap={0.5}>
                          <Typography
                            color={theme.palette.neutral[200]}
                            variant="smLight"
                          >
                            Modo de exibição
                          </Typography>
                          <Tooltip
                            title={<DisplayModeTooltip />}
                            arrow
                            placement="top"
                            slotProps={{
                              tooltip: {
                                sx: {
                                  maxWidth: 340,
                                  backgroundColor: theme.palette.neutral[200],
                                  p: 1
                                }
                              }
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                display: 'inline-flex',
                                cursor: 'pointer'
                              }}
                            >
                              <Icon
                                icon="info"
                                sx={{
                                  fontSize: '16px',
                                  color: theme.palette.neutral[500]
                                }}
                              />
                            </Box>
                          </Tooltip>
                        </Stack>
                      }
                      fullWidth
                    >
                      <MenuItem value="list">
                        <Typography variant="smLight">Lista</Typography>
                      </MenuItem>
                      <MenuItem value="dropdown">
                        <Typography variant="smLight">
                          Lista suspensa
                        </Typography>
                      </MenuItem>
                    </Select>
                  )}
                />
              )}
              {field_type === 'upload' && (
                <>
                  <Box mt="16px" mb="16px" borderTop="1px solid #E3E6E3" />
                  <Controller
                    control={control}
                    name="accepted_types"
                    render={({ field, fieldState: { error } }) => {
                      const selected = (field.value || []) as string[]

                      const toggleType = (type: UploadFileType) => {
                        const next = selected.includes(type)
                          ? selected.filter((item) => item !== type)
                          : [...selected, type]

                        // Mantém a ordem fixa dos grupos
                        field.onChange(
                          UPLOAD_FILE_TYPE_KEYS.filter((key) =>
                            next.includes(key)
                          )
                        )
                      }

                      return (
                        <Stack gap={0.5}>
                          <Typography
                            color={theme.palette.neutral[200]}
                            variant="smLight"
                          >
                            Tipos de arquivo aceitos
                          </Typography>
                          <Stack
                            direction={isSM ? 'column' : 'row'}
                            flexWrap="wrap"
                            columnGap={2}
                          >
                            {UPLOAD_FILE_TYPE_KEYS.map((type) => (
                              <FormControlLabel
                                key={type}
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={selected.includes(type)}
                                    onChange={() => toggleType(type)}
                                  />
                                }
                                label={
                                  <Typography variant="smLight">
                                    {UPLOAD_FILE_TYPES[type].label}
                                  </Typography>
                                }
                              />
                            ))}
                          </Stack>
                          {error?.message && (
                            <Typography
                              color={theme.palette.error[300]}
                              variant="xsBold"
                            >
                              {error.message}
                            </Typography>
                          )}
                        </Stack>
                      )
                    }}
                  />
                  <Controller
                    control={control}
                    name="multiple"
                    render={({ field }) => (
                      <FormControlLabel
                        sx={{ ml: 0, gap: 1 }}
                        control={
                          <Switch
                            checked={!!field.value}
                            onChange={(event) =>
                              field.onChange(event.target.checked)
                            }
                          />
                        }
                        label={
                          <Typography variant="smLight">
                            Permitir vários arquivos
                          </Typography>
                        }
                      />
                    )}
                  />
                  {watch('multiple') && (
                    <Controller
                      control={control}
                      name="max_files"
                      render={({ field, fieldState }) => (
                        <Textfield
                          name={field.name}
                          inputRef={field.ref}
                          onBlur={field.onBlur}
                          type="number"
                          value={field.value ?? ''}
                          onChange={(event) => {
                            const parsed = parseInt(event.target.value, 10)
                            field.onChange(isNaN(parsed) ? '' : parsed)
                          }}
                          inputProps={{
                            min: UPLOAD_MIN_MULTIPLE_FILES,
                            max: UPLOAD_MAX_FILES_LIMIT,
                            step: 1
                          }}
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          label={
                            <Typography
                              color={theme.palette.neutral[200]}
                              variant="smLight"
                            >
                              Quantidade máxima de arquivos
                            </Typography>
                          }
                          placeholder={`De ${UPLOAD_MIN_MULTIPLE_FILES} a ${UPLOAD_MAX_FILES_LIMIT}`}
                          fullWidth
                        />
                      )}
                    />
                  )}
                  <Typography
                    variant="smLight"
                    color={theme.palette.neutral[400]}
                  >
                    {`Cada arquivo pode ter até ${formatUploadMaxSize(
                      UPLOAD_MAX_FILE_SIZE
                    )}.`}
                  </Typography>
                </>
              )}
              {field_type === 'radio' || field_type === 'checkbox' ? (
                <>
                  <Box mt="16px" mb="16px" borderTop="1px solid #E3E6E3" />
                  <Controller
                    control={control}
                    name="options"
                    render={({ field, fieldState: { error } }) => {
                      const onChange = (
                        event: React.ChangeEvent<
                          HTMLInputElement | HTMLTextAreaElement
                        >,
                        uuid: string
                      ) => {
                        const newOptions = field?.value?.map((item: any) =>
                          item.uuid === uuid
                            ? {
                                ...item,
                                label: event.target.value,
                                value: normalizeString(event.target.value)
                              }
                            : item
                        )
                        field.onChange(newOptions)
                      }

                      const addOption = () => {
                        field.onChange([
                          ...(field?.value || []),
                          {
                            label: '',
                            value: '',
                            uuid: uuid()
                          }
                        ])
                      }

                      return (
                        <Box display="flex" flexDirection="column" gap={1.5}>
                          {field?.value?.map((option, index) => {
                            const errorLocal =
                              error !== undefined ? (error as any)[index] : null

                            return (
                              <Box
                                display="flex"
                                alignItems="center"
                                gap={0.5}
                                key={index}
                              >
                                <Textfield
                                  value={option.label}
                                  label={
                                    <Stack
                                      direction="row"
                                      alignItems="end"
                                      gap={0.5}
                                    >
                                      <Typography
                                        color={theme.palette.neutral[200]}
                                        variant="smLight"
                                      >
                                        {`Opção ${index + 1}`}
                                      </Typography>
                                    </Stack>
                                  }
                                  placeholder="Digite aqui a opção"
                                  fullWidth
                                  name="label"
                                  onChange={(e) => onChange(e, option.uuid)}
                                  error={!!errorLocal}
                                  helperText={errorLocal?.label?.message}
                                />
                                <Icon
                                  onClick={() => {
                                    const currentFieldType = watch('field_type')
                                    const canDelete =
                                      currentFieldType === 'radio' ||
                                      currentFieldType === 'checkbox'
                                        ? field?.value &&
                                          field?.value?.length > 2
                                        : field?.value &&
                                          field?.value?.length > 1

                                    if (canDelete) {
                                      handleDeleteOption(option.uuid)
                                    }
                                  }}
                                  icon="close"
                                  sx={{
                                    mt: errorLocal ? '0px' : '22px',

                                    width: '12px',
                                    ...(() => {
                                      const currentFieldType =
                                        watch('field_type')
                                      const canDelete =
                                        currentFieldType === 'radio' ||
                                        currentFieldType === 'checkbox'
                                          ? field?.value &&
                                            field?.value?.length > 2
                                          : field?.value &&
                                            field?.value?.length > 1

                                      return canDelete
                                        ? {
                                            cursor: 'pointer',
                                            color: 'black'
                                          }
                                        : {
                                            cursor: 'not-allowed',
                                            color: theme.palette.neutral[600]
                                          }
                                    })()
                                  }}
                                />
                              </Box>
                            )
                          })}
                          <Box
                            mt="4px"
                            mb="4px"
                            borderTop="1px solid #E3E6E3"
                          />
                          <Stack direction="row" justifyContent="start">
                            <Stack
                              onClick={addOption}
                              direction="row"
                              gap="5px"
                              sx={{
                                cursor: 'pointer'
                              }}
                              alignItems="center"
                            >
                              <Icon
                                icon="add"
                                sx={{
                                  color: theme.palette.neutral[400],
                                  fontSize: '18px'
                                }}
                              />
                              <Typography
                                sx={{
                                  color: theme.palette.neutral[400],
                                  fontWeight: '600',
                                  fontSize: '14px'
                                }}
                              >
                                Adicionar opção
                              </Typography>
                            </Stack>
                          </Stack>

                          {error?.message && (
                            <Typography
                              color={(theme) => theme.palette.error[300]}
                              variant="smBold"
                            >
                              {error.message}
                            </Typography>
                          )}
                        </Box>
                      )
                    }}
                  />
                </>
              ) : null}
              {field_type === 'accept' && !!watch('type') ? (
                <Box display="flex" flexDirection="column" gap={1.5}>
                  <Controller
                    control={control}
                    name="accept_text"
                    render={({ field, fieldState }) => (
                      <Textfield
                        {...field}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        label={
                          <Stack direction="row" alignItems="end" gap={0.5}>
                            <Typography
                              color={theme.palette.neutral[200]}
                              variant="smLight"
                            >
                              Aceite dos termos
                            </Typography>
                          </Stack>
                        }
                        placeholder="Digite aqui o texto do aceite"
                        fullWidth
                      />
                    )}
                  />
                  <RadioBoxContainer
                    bodyPadding={acceptTypeValue === 'file' ? '2px 16px' : 2}
                    headerContent={
                      <RadioGroup
                        sx={{
                          justifyContent: 'start',
                          alignContent: 'end'
                        }}
                        row
                        value={acceptTypeValue}
                        onChange={(event) => {
                          setAcceptTypeValue(
                            event.target.value as 'text' | 'link' | 'file'
                          )
                        }}
                      >
                        <FormControlLabel
                          value={'text'}
                          control={<Radio />}
                          label={
                            <Typography
                              variant={
                                acceptTypeValue !== 'text'
                                  ? 'smLight'
                                  : 'smBold'
                              }
                            >
                              Digitar
                            </Typography>
                          }
                        />
                        <FormControlLabel
                          value={'link'}
                          control={<Radio />}
                          label={
                            <Typography
                              variant={
                                acceptTypeValue !== 'link'
                                  ? 'smLight'
                                  : 'smBold'
                              }
                            >
                              Link de redirecionamento
                            </Typography>
                          }
                        />
                        <FormControlLabel
                          value={'file'}
                          control={<Radio />}
                          label={
                            <Typography
                              variant={
                                acceptTypeValue !== 'file'
                                  ? 'smLight'
                                  : 'smBold'
                              }
                            >
                              Upload
                            </Typography>
                          }
                        />
                      </RadioGroup>
                    }
                  >
                    {acceptTypeValue === 'text' && (
                      <Controller
                        control={control}
                        name="value"
                        render={({ field, formState: { errors } }) => (
                          <Textarea
                            {...field}
                            value={watch('type') === 'text' ? field.value : ''}
                            onChange={(event) => {
                              setValue('type', 'text')
                              resetFiles()
                              field.onChange(event.target.value)
                            }}
                            style={{
                              height: '120px',
                              resize: 'vertical',
                              maxHeight: '50vh'
                            }}
                            label={
                              <Stack direction="row" alignItems="end" gap={0.5}>
                                <Typography
                                  color={theme.palette.neutral[200]}
                                  variant="smLight"
                                >
                                  Digite o termo que você quer que seu cliente
                                  aceite
                                </Typography>
                              </Stack>
                            }
                            placeholder="Digite aqui os termos e condições"
                            fullWidth
                            error={!!errors?.value}
                            helperText={errors?.value?.message}
                          />
                        )}
                      />
                    )}
                    {acceptTypeValue === 'link' && (
                      <Controller
                        control={control}
                        name="value"
                        render={({ field, formState: { errors } }) => (
                          <Textfield
                            {...field}
                            value={watch('type') === 'link' ? field.value : ''}
                            onChange={(event) => {
                              setValue('type', 'link')
                              resetFiles()
                              field.onChange(event.target.value)
                            }}
                            label={
                              <Stack direction="row" alignItems="end" gap={0.5}>
                                <Typography
                                  color={theme.palette.neutral[200]}
                                  variant="smLight"
                                >
                                  Caso você tenha um Link com os termos, pode
                                  adicioná-lo aqui
                                </Typography>
                              </Stack>
                            }
                            placeholder="Link de redirecionamento para os termos e condições"
                            fullWidth
                            error={!!errors?.value}
                            helperText={errors?.value?.message}
                          />
                        )}
                      />
                    )}
                    {acceptTypeValue === 'file' && (
                      <>
                        <p
                          style={{
                            fontSize: '14px',
                            marginBottom: '2px',
                            marginLeft: '2px',
                            color: theme.palette.neutral[200]
                          }}
                        >
                          Faça upload do documento com seus termos
                        </p>
                        <UploadComponent
                          setLoadingExternal={setUploadIsLoading}
                          type="custom_fields"
                          maxFiles={1}
                          title={
                            <Stack direction="row" gap={0.5}>
                              <Typography variant="smBold">
                                Anexar um arquivo
                              </Typography>
                            </Stack>
                          }
                          disabledText="Remova o documento atual para anexar outro"
                          disabled={files.length === 1}
                          subTitle="Clique aqui ou arraste os arquivos para anexá-los"
                          docType="terms"
                          isLoading={uploadIsLoading}
                          ref={uploadRef}
                          setStateExternal={setFiles}
                          error={!!formState.errors?.value}
                          helperText={formState.errors?.value?.message}
                          accept={{
                            'application/pdf': ['.pdf'],
                            'application/msword': ['.doc'],
                            'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                              ['.docx']
                          }}
                        />
                      </>
                    )}
                  </RadioBoxContainer>
                </Box>
              ) : null}
            </S.Wrapper>
            {data && (
              <Box mt={2}>
                <Typography color={theme.palette.neutral[500]}>
                  *Atenção! As mudanças efetuadas serão replicadas em todos os
                  lugares que o campo estiver incluso
                </Typography>
              </Box>
            )}
          </ModalContent>
          <ModalFooter>
            <Box display="flex" flex={isSM ? 1 : 0} gap={1.25}>
              <Button
                variantButton="neutral"
                disabled={loadingSubmit}
                onClick={onClose}
                fullWidth
              >
                Cancelar
              </Button>
              <Button
                disabled={loadingSubmit || uploadIsLoading}
                loading={loadingSubmit || uploadIsLoading}
                variantButton="primary"
                type="button"
                onClick={handleClickToSubmit}
                fullWidth
              >
                Salvar
              </Button>
            </Box>
          </ModalFooter>
        </ModalBody>
      )}
      <Dialog
        icon="edit"
        title="Atenção"
        iconColor={theme.palette.neutral[200]}
        description="As mudanças efetuadas serão replicadas em todos os lugares que o campo estiver incluso"
        PaperProps={{
          sx: {
            width: '380px'
          }
        }}
        setIsOpenDialog={setDialogOpen}
        isOpenDialog={dialogOpen}
        cancelButton
      >
        <Button
          autoFocus
          disabled={loading}
          onClick={handleSubmit(onSubmit, toastFirstError)}
          loading={loading}
          variantButton="primary"
          fullWidth
        >
          Salvar
        </Button>
      </Dialog>
    </ModalComponent>
  )
}

export default Modal
